'use client';

import React from 'react';
import { Button, Input, Link, Form } from '@heroui/react';
import { Icon } from '@iconify/react';
import { Formik, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router';
import { useTranslations } from '../../contexts/translation.context';
import RightImage from './imagepartner.jpg';
import { AcmeIcon } from '../../client/sign-in/acme';
import { parseApiError } from '../../utils/errorParser';
import './index.css';
import { Typography, CustomCheckbox, AlertBlock } from '../../components';
import { buttonColors } from '../../theme/colors';
import { LogoPartnerIc } from '../../components/icons';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';

type PartnerSignInFormValues = {
  email: string;
  password: string;
  remember: boolean;
};

const PARTNER_REMEMBER_KEY = 'partnerRememberMe';
const PARTNER_CREDENTIALS_KEY = 'partnerCredentials';
const LEGACY_REMEMBER_KEY = 'rememberMe';

const getStoredPartnerValues = (): PartnerSignInFormValues => {
  if (typeof window === 'undefined') {
    return { email: '', password: '', remember: false };
  }

  const rememberRaw =
    localStorage.getItem(PARTNER_REMEMBER_KEY) ?? localStorage.getItem(LEGACY_REMEMBER_KEY);
  const remember = rememberRaw === 'true';

  if (!remember) {
    return { email: '', password: '', remember: false };
  }

  const storedCredentials = localStorage.getItem(PARTNER_CREDENTIALS_KEY);
  if (storedCredentials) {
    try {
      const parsed = JSON.parse(storedCredentials) ?? {};
      return {
        email: typeof parsed.email === 'string' ? parsed.email : '',
        password: typeof parsed.password === 'string' ? parsed.password : '',
        remember: true,
      };
    } catch {
      localStorage.removeItem(PARTNER_CREDENTIALS_KEY);
    }
  }

  return { email: '', password: '', remember: true };
};

const persistPartnerValues = (values: PartnerSignInFormValues) => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.removeItem(LEGACY_REMEMBER_KEY);

  if (values.remember) {
    localStorage.setItem(PARTNER_REMEMBER_KEY, 'true');
    localStorage.setItem(
      PARTNER_CREDENTIALS_KEY,
      JSON.stringify({
        email: values.email ?? '',
        password: values.password ?? '',
      })
    );
  } else {
    localStorage.setItem(PARTNER_REMEMBER_KEY, 'false');
    localStorage.removeItem(PARTNER_CREDENTIALS_KEY);
  }
};

// Validation schema for partner sign in - UPDATED v2
const getSignInSchema = (t: (key: string) => string) => {
  return Yup.object().shape({
    email: Yup.string()
      .email(t('auth.validation.emailInvalid'))
      .required(t('auth.validation.emailRequired')),
    password: Yup.string()
      .min(6, t('auth.validation.passwordMin'))
      .max(25, t('auth.validation.passwordMax'))
      .required(t('auth.validation.passwordRequired')),
    remember: Yup.boolean(),
  });
};
export default function PartnerSignIn() {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [currentStep, setCurrentStep] = React.useState<'select' | 'signin'>('select');
  const [initialFormValues] = React.useState<PartnerSignInFormValues>(() =>
    getStoredPartnerValues()
  );
  const lastEnteredValuesRef = React.useRef<PartnerSignInFormValues>(initialFormValues);
  const updateRememberedValues = React.useCallback((nextValues: PartnerSignInFormValues) => {
    lastEnteredValuesRef.current = nextValues;
    persistPartnerValues(nextValues);
  }, []);

  const { login, loginWithToken, googleAuthPartner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslations();

  // Авторизация по токену из query-параметра
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      loginWithToken(token, 'partner')
        .then(() => {
          navigate('/partner/dashboard', { replace: true });
        })
        .catch((err) => {
          setError(t('auth.errors.tokenAuthError'));
        });
    }
    // eslint-disable-next-line
  }, [location.search]);

  // Check for success message from other pages and navigation state
  React.useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the message from location state
      navigate(location.pathname, { replace: true });
    }

    // If coming from client with skipSelect, skip the select step
    if (location.state?.skipSelect) {
      setCurrentStep('signin');
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  const handleMarketerSelect = () => {
    setCurrentStep('signin');
  };

  const handleSubmit = async (
    values: PartnerSignInFormValues,
    { setSubmitting, setValues }: FormikHelpers<PartnerSignInFormValues>
  ) => {
    const submittedValues = { ...values };
    lastEnteredValuesRef.current = submittedValues;
    try {
      setIsLoading(true);
      setError(null);

      await login(values.email, values.password, 'partner');

      // Redirect to partner dashboard (ignore previous location)
      navigate('/partner/dashboard', { replace: true });
    } catch (error: any) {
      console.error('Login failed:', error);
      setError(parseApiError(error, t('auth.errors.loginError')));
      setTimeout(() => {
        setValues(submittedValues, false);
      }, 0);
    } finally {
      updateRememberedValues(lastEnteredValuesRef.current);
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center bg-white px-4 md:px-6 pt-[5.25rem] pb-4 md:py-0">
      <div className="flex flex-col justify-end md:justify-normal fixed top-0 left-0 w-full z-50 bg-white md:bg-transparent border-b border-solid border-neutral-12 px-4 py-6 md:px-12 md:pt-12 md:border-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center justify-center w-[4.75rem] h-[1.375rem] md:w-[7.5rem] md:h-9 shrink-0">
            <LogoPartnerIc className="size-full" />
          </div>
          <div className={`${currentStep !== 'select' ? 'md:hidden' : ''}`}>
            <LanguageSwitcher variant="ghost" />
          </div>
        </div>
      </div>

      <div className="flex w-full md:min-h-[calc(100vh-48px)] flex-col justify-center md:flex-row md:items-stretch">
        <div
          className={`flex w-full items-center justify-center bg-background py-6 md:px-6 md:py-12 ${
            currentStep === 'select' ? 'md:w-full' : 'md:flex-1'
          }`}
        >
          {currentStep === 'select' ? (
            <PartnerAuthSelectStep
              t={t}
              onCreatorSelect={() =>
                navigate('/client/sign-in', { state: { userType: 'creator', skipSelect: true } })
              }
              onMarketerSelect={handleMarketerSelect}
              onNavigateSignUp={() => navigate('/partner/sign-up')}
            />
          ) : (
            <PartnerSignInFormStep
              t={t}
              error={error}
              success={success}
              isLoading={isLoading}
              initialValues={initialFormValues}
              onSubmit={handleSubmit}
              updateRememberedValues={updateRememberedValues}
              googleAuthPartner={googleAuthPartner}
              onNavigateClientSignIn={() =>
                navigate('/client/sign-in', { state: { userType: 'creator', skipSelect: true } })
              }
            />
          )}
        </div>

        {currentStep !== 'select' && (
          <div className="hidden w-full items-stretch gap-12 md:flex md:flex-1">
            <div className="shrink-0">
              <LanguageSwitcher variant="ghost" />
            </div>
            <div
              className="hidden h-full flex-1 overflow-hidden rounded-8 md:block"
              style={{
                backgroundImage: `url(${RightImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

type PartnerAuthSelectStepProps = {
  t: (key: string) => string;
  onCreatorSelect: () => void;
  onMarketerSelect: () => void;
  onNavigateSignUp: () => void;
};

const PartnerAuthSelectStep: React.FC<PartnerAuthSelectStepProps> = React.memo(
  ({ t, onCreatorSelect, onMarketerSelect, onNavigateSignUp }) => (
    <div className="flex w-full max-w-[26.5rem] flex-col items-center gap-6 md:gap-8 md:p-4">
      <div className="w-full text-center flex flex-col gap-1 md:gap-2">
        <Typography variant="h3">{t('auth.login')}</Typography>
        <Typography className="text-auth-muted" variant="body1">
          {t('auth.selectAccountType')}
        </Typography>
      </div>

      <div className="flex w-full flex-col gap-2 md:gap-3">
        <div
          onClick={onCreatorSelect}
          className="w-full cursor-pointer min-h-[4.75rem] flex items-center bg-auth-surface rounded-2 p-4 hover:bg-auth-surface-hover transition-colors user-type-card"
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center size-6 md:size-9 shrink-0">
              <Icon
                width={36}
                height={36}
                icon="solar:user-rounded-linear"
                className="text-2xl text-auth-ink size-full"
              />
            </div>
            <Typography variant="h7">
              {t('sidebar.creator')} / {t('sidebar.agency')}
            </Typography>
          </div>
        </div>

        <div
          onClick={onMarketerSelect}
          className="w-full cursor-pointer min-h-[4.75rem] flex items-center bg-auth-surface rounded-2 p-4 hover:bg-auth-surface-hover transition-colors user-type-card"
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center size-6 md:size-9 shrink-0">
              <Icon
                width={36}
                height={36}
                icon="solar:user-id-outline"
                className="text-2xl text-auth-ink size-full"
              />
            </div>
            <Typography variant="h7">{t('sidebar.marketer')}</Typography>
          </div>
        </div>
      </div>

      <div className="w-full flex justify-between items-center">
        <p className="text-sm text-auth-muted">{t('auth.noAccount')}</p>
        <Button
          variant="light"
          size="sm"
          onPress={onNavigateSignUp}
          className="text-sm p-0 h-5 min-w-0"
          style={{ color: buttonColors.partner.primary.background }}
        >
          {t('auth.signUp')}
        </Button>
      </div>
    </div>
  )
);

type PartnerSignInFormStepProps = {
  t: (key: string) => string;
  error: string | null;
  success: string | null;
  isLoading: boolean;
  initialValues: PartnerSignInFormValues;
  onSubmit: (
    values: PartnerSignInFormValues,
    helpers: FormikHelpers<PartnerSignInFormValues>
  ) => Promise<void>;
  updateRememberedValues: (values: PartnerSignInFormValues) => void;
  googleAuthPartner: () => void;
  onNavigateClientSignIn: () => void;
};

const PartnerSignInFormStep: React.FC<PartnerSignInFormStepProps> = React.memo(
  ({
    t,
    error,
    success,
    isLoading,
    initialValues,
    onSubmit,
    updateRememberedValues,
    googleAuthPartner,
    onNavigateClientSignIn,
  }) => {
    const [localIsVisible, setLocalIsVisible] = React.useState(false);

    const localToggleVisibility = () => setLocalIsVisible(!localIsVisible);

    return (
      <div className="flex w-full max-w-[26.5rem] flex-col items-center gap-2 md:gap-4">
        <div className="w-full flex flex-col text-center mb-4 gap-1 md:gap-2">
          <Typography variant="h3">
            {t('auth.login')} / {t('sidebar.marketer')}
          </Typography>
          <Typography className="text-auth-muted" variant="body1">
            {t('auth.enterData')}
          </Typography>
        </div>

        {error && (
          <div className="w-full mb-4 bg-auth-error-bg border-2 border-auth-error-border rounded-3 p-4 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="size-10 bg-auth-error-bg border border-auth-error-border rounded-[1.875rem] flex items-center justify-center flex-shrink-0">
                <Icon
                  icon="mingcute:information-fill"
                  width="19"
                  height="19"
                  className="text-auth-error-text"
                />
              </div>
              <p className="text-auth-error-text text-sm font-medium leading-5">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <AlertBlock
            type="rounded"
            data={[{ severity: 'success', description: success }]}
            className="w-full mb-4"
          />
        )}

        <Formik<PartnerSignInFormValues>
          key="partner-signin-form"
          initialValues={initialValues}
          validationSchema={getSignInSchema(t)}
          onSubmit={onSubmit}
          enableReinitialize={false}
        >
          {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
            <form
              className="flex w-full flex-col gap-4"
              onSubmit={handleSubmit}
              noValidate={true}
              lang="ru"
              data-disable-validation="true"
              autoComplete="off"
              onInvalid={(e) => e.preventDefault()}
            >
              <div className="flex flex-col gap-1 w-full">
                <Input
                  label={t('labels.email')}
                  name="email"
                  placeholder={t('placeholders.enterEmail')}
                  type="email"
                  variant="bordered"
                  value={values.email}
                  onChange={(e) => {
                    if (e.nativeEvent?.isTrusted === false) {
                      return;
                    }
                    const nextEmail = e.target.value;
                    setFieldValue('email', nextEmail);
                    updateRememberedValues({
                      ...values,
                      email: nextEmail,
                    });
                  }}
                  labelPlacement="inside"
                  className="w-full custom-required-label-partner"
                  classNames={{
                    inputWrapper: 'h-14',
                    input: 'text-black',
                  }}
                  autoComplete="email"
                  spellCheck="false"
                  data-no-validate="true"
                  inputMode="email"
                  onInvalid={(e) => e.preventDefault()}
                  onInput={(e) => {
                    (e.target as HTMLInputElement).setCustomValidity('');
                  }}
                  title=""
                  required={false}
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-xs" />
              </div>

              <div className="flex flex-col gap-1 w-full">
                <Input
                  endContent={
                    <button type="button" onClick={localToggleVisibility}>
                      {localIsVisible ? (
                        <Icon
                          className="pointer-events-none text-2xl text-default-400"
                          icon="solar:eye-closed-linear"
                        />
                      ) : (
                        <Icon
                          className="pointer-events-none text-2xl text-default-400"
                          icon="solar:eye-bold"
                        />
                      )}
                    </button>
                  }
                  label={t('labels.password')}
                  name="password"
                  placeholder={t('placeholders.enterPassword')}
                  type={localIsVisible ? 'text' : 'password'}
                  variant="bordered"
                  value={values.password}
                  onChange={(e) => {
                    if (e.nativeEvent?.isTrusted === false) {
                      return;
                    }
                    const nextPassword = e.target.value;
                    setFieldValue('password', nextPassword);
                    updateRememberedValues({
                      ...values,
                      password: nextPassword,
                    });
                  }}
                  labelPlacement="inside"
                  className="w-full custom-required-label-partner"
                  classNames={{
                    inputWrapper: 'h-14',
                    input: 'text-black',
                  }}
                  autoComplete="current-password"
                  spellCheck="false"
                  data-no-validate="true"
                  onInvalid={(e) => e.preventDefault()}
                  onInput={(e) => {
                    (e.target as HTMLInputElement).setCustomValidity('');
                  }}
                  pattern=".*"
                  title=""
                  required={false}
                />
                <ErrorMessage name="password" component="div" className="text-red-500 text-xs" />
              </div>

              <div className="flex w-full items-center justify-between px-1">
                <CustomCheckbox
                  name="remember"
                  size="sm"
                  isSelected={values.remember}
                  onValueChange={(checked: boolean) => {
                    const nextValues = {
                      ...values,
                      remember: checked,
                    };
                    setFieldValue('remember', checked);
                    updateRememberedValues(nextValues);
                  }}
                >
                  {t('auth.rememberMe')}
                </CustomCheckbox>
                <Link
                  href="/partner/forgot-password"
                  size="sm"
                  style={{ color: buttonColors.partner.primary.background }}
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
              <Button
                className="w-full font-semibold h-12 mt-4 md:mt-2 text-base"
                type="submit"
                isLoading={isLoading || isSubmitting}
                disabled={isLoading || isSubmitting}
                style={{
                  backgroundColor: buttonColors.partner.primary.background,
                  color: buttonColors.partner.primary.text,
                }}
              >
                {isLoading || isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
              </Button>
            </form>
          )}
        </Formik>

        <div className="flex w-full flex-col gap-2">
          <Button
            startContent={<Icon icon="flat-color-icons:google" width={24} />}
            variant="bordered"
            className="h-12 font-semibold text-base"
            onPress={googleAuthPartner}
          >
            {t('auth.continueWithGoogle')}
          </Button>
        </div>

        <div className="flex w-full flex-col gap-2">
          <div
            onClick={onNavigateClientSignIn}
            className="w-full cursor-pointer h-12 bg-auth-surface rounded-2 p-3 hover:bg-auth-surface-hover transition-colors user-type-card flex items-center justify-center"
          >
            <div className="flex items-center gap-2">
              <p className="text-base font-semibold text-auth-ink">{t('auth.loginAsCreator')}</p>
            </div>
          </div>
        </div>

        <div className="w-full flex justify-between md:mt-0 mt-4">
          <p className="text-small">{t('auth.noAccount')}&nbsp;</p>
          <Link
            href="/partner/sign-up"
            size="sm"
            style={{ color: buttonColors.partner.primary.background }}
          >
            {t('auth.signUp')}
          </Link>
        </div>
      </div>
    );
  }
);
